import { useQuery } from "@tanstack/react-query";
import DeviceMetricsService from "./DeviceMetricsService";
import type { DeviceMetricsResponse, MetricsTimeRange } from "../generated/models/DeviceMetrics";

/**
 * Calculate limit based on time range.
 * Assuming ~5-minute intervals: 24h=288, 7d=2016, 30d=8640
 */
const getMetricsLimit = (timeRange: MetricsTimeRange): number => {
    switch (timeRange) {
        case '24h':
            return 300;
        case '7d':
            return 2100;
        case '30d':
            return 9000;
        default:
            return 300;
    }
};

/**
 * Hook to fetch device metrics history by hostname.
 */
export const useDeviceMetricsQuery = (
    hostname: string | null,
    timeRange: MetricsTimeRange = '24h'
) => {
    const limit = getMetricsLimit(timeRange);

    return useQuery<DeviceMetricsResponse[], Error>({
        queryKey: ["deviceMetrics", hostname, timeRange],
        queryFn: () => DeviceMetricsService.getMetricsByHostname(hostname!, limit),
        enabled: !!hostname,
        staleTime: 60000, // 1 minute
        refetchInterval: 60000, // Auto-refresh every minute
    });
};

/**
 * Hook to fetch device metrics history by device ID.
 */
export const useDeviceMetricsByIdQuery = (
    deviceId: number | null,
    timeRange: MetricsTimeRange = '24h'
) => {
    const limit = getMetricsLimit(timeRange);

    return useQuery<DeviceMetricsResponse[], Error>({
        queryKey: ["deviceMetricsById", deviceId, timeRange],
        queryFn: () => DeviceMetricsService.getMetricsByDeviceId(deviceId!, limit),
        enabled: !!deviceId,
        staleTime: 60000, // 1 minute
        refetchInterval: 60000, // Auto-refresh every minute
    });
};

/**
 * Hook to fetch metrics overview (latest for each device).
 */
export const useDeviceMetricsOverviewQuery = () => {
    return useQuery<DeviceMetricsResponse[], Error>({
        queryKey: ["deviceMetricsOverview"],
        queryFn: () => DeviceMetricsService.getMetricsOverview(),
        staleTime: 30000, // 30 seconds
    });
};
